import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { AvailabilityCalendar, AvailabilityCalendarDatePicker } from "mueller-controls";
import MCard from "components/Shared/MCard.tsx";
import moment from "moment";
import { getAppraiserListByAppraisalId } from "actions/appraiser";
import { getCalendarEventsByEmployeeId } from "requests/employeeCalendarEvent";
import { AppraisalWorkflowStatusCode } from "typeConstants/appraisalWorkflowStatusCodes";
import { getHolidaysBetweenDates } from "requests/holiday/index.ts";
import { getHolidayMouseoverText } from "components/Employee/EmployeeCalendar.tsx";

const AppraiserAvailabilityView = props => {
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [monthsLoaded, setMonthsLoaded] = useState([]);
  const [currentDate, setCurrentDate] = useState(moment());
  const mounted = useRef(true);
  const [holidays, setHolidays] = useState();

  useEffect(() => {
    const holidayLoadRangeYears = 2;
    getHolidaysBetweenDates(moment().subtract(holidayLoadRangeYears, "year").toDate(), moment().add(holidayLoadRangeYears, "year").toDate(), setHolidays, mounted);
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    props.getAppraiserListByAppraisalId(props.controlIdNumber);
  }, [props.controlIdNumber]);

  useEffect(() => {
    loadAllEntriesForAppraisers(currentDate.clone().startOf("month"));
  }, [props.assignableAppraisers]);

  const getMonthNumber = momentDate => parseInt(momentDate.format("YYYYMM"));
  const maxMonth = getMonthNumber(moment());

  const onDateChanged = date => {
    let startOfMonth = date.clone().startOf("month");
    let endOfMonth = date.clone().endOf("month");
    let monthNum = getMonthNumber(startOfMonth);
    setCurrentDate(date);

    if (monthNum < maxMonth && !monthsLoaded.some(m => m === monthNum)) {
      setMonthsLoaded([...monthsLoaded, monthNum]);
      loadAllEntriesForAppraisers(startOfMonth, endOfMonth);
    }
  };

  const loadAllEntriesForAppraisers = (startDate, endDate) => {
    let availableAppraisers = props.assignableAppraisers?.appraisersAvailable ?? [];
    let inactiveAppraisesrs = props.assignableAppraisers?.appraisersIneligible ?? [];
    let allAppraisers = [...availableAppraisers, ...inactiveAppraisesrs];
    allAppraisers?.forEach(a => {
      getCalendarEventsByEmployeeId(a.employeeId, startDate, endDate, entries => combineEntries(entries), null, mounted);
    });
  };

  // set the new entries in state, combine with any old entries that we didnt get fresh data for
  const combineEntries = serverEntries => {
    setCalendarEntries(previousEntries => [
      ...serverEntries,
      ...previousEntries.filter(e => !serverEntries.some(se => se.timeType === e.timeType && se.id === e.id)),
    ]);
  };

  const mapAppraisersForCalendar = () => {
    let activeAppraisers = props.assignableAppraisers?.appraisersAvailable?.map(a => ({
      name: (a.isSalary ? "S-" : "") + a.fullName + ` (${a.performanceGrade}; ${a.proximityToSubject} mi)`,
      employeeId: a.employeeId,
    })) ?? [];
    let inactiveAppraisers = props.assignableAppraisers?.appraisersIneligible?.map(a => ({
      name: (a.isSalary ? "S-" : "") + a.fullName + ` (${a.performanceGrade}; ${a.proximityToSubject} mi)`,
      employeeId: a.employeeId,
      className: "ineligible",
    })) ?? [];
    return [...activeAppraisers, ...inactiveAppraisers];
  };
  
  return (
    <MCard title="Appraiser List" className="employee-availability-calendar">
      <AvailabilityCalendarDatePicker date={currentDate} onDateChanged={onDateChanged} />
      <AvailabilityCalendar 
        date={currentDate} 
        employees={mapAppraisersForCalendar() ?? []} 
        calendarEntries={calendarEntries}
        startTime={0}
        endTime={24}
        scrollStartTime={8}
        holidays={holidays}
        getHolidayMouseoverText={getHolidayMouseoverText}
      />
    </MCard>
  );
};

AppraiserAvailabilityView.propTypes = {
  controlIdNumber: PropTypes.number,
  // Redux
  assignableAppraisers: PropTypes.object,
  getAppraiserListByAppraisalId: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
      assignableAppraisers: state.caseDetails.assignableAppraisers,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getAppraiserListByAppraisalId(controlIdNumber) {
      dispatch(getAppraiserListByAppraisalId(controlIdNumber));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AppraiserAvailabilityView);

export const AppraiserAvailabilityViewStatusCodes = [
  AppraisalWorkflowStatusCode.AVAILABLE_TO_APPRAISER,
  AppraisalWorkflowStatusCode.REJECTED_TO_APPRAISER,
  AppraisalWorkflowStatusCode.CUSTOMER_REJECTED,
  AppraisalWorkflowStatusCode.APPRAISER_RECONSIDERING,
  AppraisalWorkflowStatusCode.VEROS_PREVIEW_RESULT_ERROR,
  AppraisalWorkflowStatusCode.VEROS_PREVIEW_ERROR_APPRAISER,
  AppraisalWorkflowStatusCode.UCDP_RESULT_RECEIVED_WITH_ERRORS,
  AppraisalWorkflowStatusCode.AWAITING_FIELD_REPORT,
  AppraisalWorkflowStatusCode.APPRAISAL_SUBMITTAL_VERIFICATION,
  AppraisalWorkflowStatusCode.VEROS_PREVIEW_REQUESTED,
  AppraisalWorkflowStatusCode.GAAR_PREVIEW_REQUESTED,
  AppraisalWorkflowStatusCode.UCDP_SUBMITTED_PENDING_RESPONSE,
  AppraisalWorkflowStatusCode.UCDP_RESULT_RECEIVED_NO_ERRORS,
  AppraisalWorkflowStatusCode.AWAITING_ELIGIBILITY_DECISION,
  AppraisalWorkflowStatusCode.PENDING_APPRAISAL_ASSIGNMENT,
  AppraisalWorkflowStatusCode.REJECTED_TO_FIELD,
  AppraisalWorkflowStatusCode.UNASSIGNED,
];